import { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { Box, Typography, Tooltip } from "@mui/material";

/**
 * ExpandableSummary Component - Displays a summary text with "Read more" option when needed
 *
 * @param {Object} props - Component props
 * @param {string} props.summary - The summary text to display
 * @param {boolean} props.isMobileView - Whether the component is being viewed on mobile
 * @param {Object} props.highlights - Optional search highlights object
 * @returns {JSX.Element} Rendered component
 */
const ExpandableSummary = ({ summary, isMobileView, highlights }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  // Style constants extracted for better readability and maintainability
  const STYLES = {
    container: { mb: 1.5, position: "relative" },
    flexContainer: { display: "flex", alignItems: "flex-start", gap: 0.75 },
    iconBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "18px",
      height: "18px",
      borderRadius: "4px",
      backgroundColor: "rgba(0, 61, 120, 0.05)",
      transition: "all 0.2s ease",
    },
    icon: {
      fontSize: "0.65rem",
      color: "rgba(0, 61, 120, 0.4)",
    },
    contentBox: { flex: 1, position: "relative" },
    typography: (isExpanded) => ({
      fontSize: isMobileView ? "12px" : "0.8rem",
      color: "#1F2937",
      lineHeight: 1.6,
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontWeight: 400,
      letterSpacing: "0.01em",
      // Only apply line clamp if not expanded
      ...(!isExpanded && {
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      }),
    }),
    readMoreButton: {
      cursor: "pointer",
      fontSize: isMobileView ? "11px" : "0.75rem",
      color: "#2563EB",
      fontWeight: 500,
      mt: 0.5,
      display: "inline-flex",
      alignItems: "center",
      fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  };

  // Memoize the check overflow function to avoid recreating on each render
  const checkOverflow = useCallback(() => {
    const element = textRef.current;
    if (element) {
      setIsOverflowing(element.scrollHeight > element.clientHeight);
    }
  }, []);

  useEffect(() => {
    // Check initial overflow state
    checkOverflow();

    // Add resize listener to recheck on window resize
    window.addEventListener("resize", checkOverflow);

    // Clean up event listener on component unmount
    return () => window.removeEventListener("resize", checkOverflow);
  }, [summary, checkOverflow]);

  // Determine the content to display (highlights or regular summary)
  const summaryContent = highlights?.Short_Summary || summary;

  return (
    <Box sx={STYLES.container}>
      <Box sx={STYLES.flexContainer}>
        <Box sx={STYLES.iconBox}>
          <Tooltip title="Short Summary" placement="top" arrow>
            <FontAwesomeIcon icon={faFileAlt} style={STYLES.icon} />
          </Tooltip>
        </Box>

        <Box sx={STYLES.contentBox}>
          <Typography
            ref={textRef}
            sx={STYLES.typography(isExpanded)}
            dangerouslySetInnerHTML={{ __html: summaryContent }}
          />

          {isOverflowing && (
            <Box
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-expanded={isExpanded}
              sx={STYLES.readMoreButton}
            >
              {isExpanded ? "Read less" : "Read more"}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExpandableSummary;
